import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Phone, MapPin, Star, ChevronRight, Mail,
  Camera, Network, Key, Wifi, Cable
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';

// Services data
const services = [
  {
    id: 'fiber-optic',
    icon: Cable,
    title: 'Fiber Optic Installation',
    titleEs: 'Instalación de Fibra Óptica',
    description: 'High-speed fiber optic installation and splicing services for maximum data transfer efficiency.',
    descriptionEs: 'Instalación y empalme de fibra óptica de alta velocidad para máxima eficiencia en transferencia de datos.',
    image: '/projects/project-17.jpeg'
  },
  {
    id: 'cctv',
    icon: Camera,
    title: 'CCTV Systems',
    titleEs: 'Sistemas CCTV',
    description: 'Advanced surveillance and security camera systems with remote monitoring capabilities.',
    descriptionEs: 'Sistemas avanzados de vigilancia y cámaras de seguridad con capacidades de monitoreo remoto.',
    image: '/projects/project-22.jpeg'
  },
  {
    id: 'structured-cabling',
    icon: Network,
    title: 'Structured Cabling',
    titleEs: 'Cableado Estructurado',
    description: 'Professional structured cabling for reliable network infrastructure.',
    descriptionEs: 'Cableado estructurado profesional para infraestructura de red confiable.',
    image: '/projects/project-3.jpeg'
  },
  {
    id: 'access-control',
    icon: Key,
    title: 'Access Control',
    titleEs: 'Control de Acceso',
    description: 'Secure access control systems to protect your facilities and assets.',
    descriptionEs: 'Sistemas seguros de control de acceso para proteger sus instalaciones y activos.',
    image: '/projects/project-34.jpeg'
  },
  {
    id: 'networking',
    icon: Wifi,
    title: 'Networking Solutions',
    titleEs: 'Soluciones de Redes',
    description: 'Complete networking solutions for seamless connectivity and communication.',
    descriptionEs: 'Soluciones de red completas para conectividad y comunicación sin interrupciones.',
    image: '/projects/project-28.jpeg'
  }
];

// Florida cities with colors
const floridaCities = [
  { name: 'Jacksonville', color: 'bg-blue-500' },
  { name: 'Orlando', color: 'bg-teal-500' },
  { name: 'Tampa', color: 'bg-emerald-500' },
  { name: 'Tallahassee', color: 'bg-yellow-500' },
  { name: 'Miami', color: 'bg-orange-500' },
  { name: 'West Palm Beach', color: 'bg-red-500' }
];

// Projects
const projectImages = [
  { src: '/projects/project-3.jpeg', title: 'Cat6 | Cat6A Cabling', titleEs: 'Cableado Cat6 | Cat6A' },
  { src: '/projects/project-17.jpeg', title: 'Fiber Optic', titleEs: 'Fibra Óptica' },
  { src: '/projects/project-22.jpeg', title: 'CCTV | IP Cameras', titleEs: 'CCTV | Cámaras IP' },
  { src: '/projects/project-28.jpeg', title: 'Networking', titleEs: 'Redes' },
  { src: '/projects/project-8.jpeg', title: 'Data Center', titleEs: 'Centro de Datos' },
  { src: '/projects/project-1.jpeg', title: 'Structured Cabling', titleEs: 'Cableado Estructurado' },
  { src: '/projects/project-18.jpeg', title: 'Fiber Testing', titleEs: 'Pruebas de Fibra' },
  { src: '/projects/project-34.jpeg', title: 'Access Control', titleEs: 'Control de Acceso' },
  { src: '/projects/project-35.jpeg', title: 'WiFi Solutions', titleEs: 'Soluciones WiFi' },
  { src: '/projects/project-9.jpeg', title: 'Cable Management', titleEs: 'Gestión de Cables' }
];

// Featured Projects
const featuredProjects = [
  {
    title: 'Data Center Structured Cabling',
    titleEs: 'Cableado Estructurado Data Center',
    category: 'Structured Cabling',
    location: 'West Palm Beach, FL',
    date: '11/13/2025',
    description: 'Complete Cat6 structured cabling installation for a data center facility. Over 200 cable runs with professional cable management.',
    descriptionEs: 'Instalación completa de cableado estructurado Cat6 para un centro de datos. Más de 200 corridas de cable con gestión profesional.',
    image: '/projects/project-3.jpeg'
  },
  {
    title: 'Commercial CCTV Security System',
    titleEs: 'Sistema CCTV Comercial',
    category: 'CCTV',
    location: 'Miami, FL',
    date: '8/19/2025',
    description: 'Comprehensive CCTV security camera installation for a commercial facility in Miami with 4K resolution cameras.',
    descriptionEs: 'Instalación completa de cámaras de seguridad CCTV para instalación comercial en Miami con cámaras 4K.',
    image: '/projects/project-22.jpeg'
  },
  {
    title: 'Fiber Optic Testing & Certification',
    titleEs: 'Pruebas y Certificación de Fibra Óptica',
    category: 'Fiber Optic',
    location: 'South Florida',
    date: '9/14/2024',
    description: 'Professional fiber optic testing using Fluke Networks equipment. OTDR testing certified to industry standards.',
    descriptionEs: 'Pruebas profesionales de fibra óptica usando equipos Fluke Networks. Pruebas OTDR certificadas.',
    image: '/projects/project-17.jpeg'
  }
];

// Testimonials
const testimonials = [
  {
    name: 'Dr. Emily Chen',
    company: 'Florida Medical Center',
    text: 'The access control system has greatly improved our security. The team understood our unique healthcare requirements perfectly.',
    textEs: 'El sistema de control de acceso ha mejorado enormemente nuestra seguridad. El equipo entendió perfectamente nuestros requisitos.'
  },
  {
    name: 'Michael Rodriguez',
    company: 'Secure Retail Group',
    text: 'Our new CCTV system has been flawless. The installation was quick, and the training provided was excellent. Great team!',
    textEs: 'Nuestro nuevo sistema CCTV ha funcionado perfectamente. La instalación fue rápida y la capacitación excelente.'
  },
  {
    name: 'Sarah Johnson',
    company: 'Tech Solutions Inc.',
    text: 'Exceptional service! The team completed our fiber optic installation ahead of schedule and within budget.',
    textEs: '¡Servicio excepcional! El equipo completó nuestra instalación de fibra óptica antes de lo previsto.'
  }
];

// Stats
const stats = [
  { value: '13+', label: 'Years Experience', labelEs: 'Años de Experiencia' },
  { value: '280+', label: 'Projects Completed', labelEs: 'Proyectos Completados' },
  { value: '25+', label: 'Certified Technicians', labelEs: 'Técnicos Certificados' },
  { value: '100%', label: 'Satisfaction Rate', labelEs: 'Tasa de Satisfacción' }
];

export default function Home() {
  const { language } = useLanguage();
  const isEs = language === 'es';

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section - Exact Abacus colors: Navy #003366 to Green #00A651 */}
      <section className="relative min-h-[550px] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Background - EXACT gradient from Abacus */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgb(0, 51, 102) 0%, rgba(0, 51, 102, 0.867) 50%, rgba(0, 166, 81, 0.25) 100%)' }} />
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgb(255, 255, 255) 35px, rgb(255, 255, 255) 36px)' }} />
        </div>
        
        {/* Wave at bottom - EXACT from Abacus */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-16" style={{ fill: 'rgb(255, 255, 255)' }}>
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25"/>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5"/>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"/>
          </svg>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 text-4xl sm:text-5xl lg:text-6xl font-bold text-white"
            >
              IQES Low Voltage Solutions
            </motion.h1>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-4 text-base sm:text-lg lg:text-xl text-white/90 max-w-5xl mx-auto leading-relaxed"
            >
              {isEs 
                ? '+13 años de experiencia brindando soluciones integrales de bajo voltaje y telecomunicaciones. Nos especializamos en el diseño, implementación y mantenimiento de infraestructura tecnológica bajo estándares internacionales de calidad, asegurando continuidad operativa, seguridad y eficiencia para organizaciones de todos los sectores.'
                : '+13 years of experience providing comprehensive low voltage and telecommunications solutions. We specialize in the design, implementation, and maintenance of technology infrastructure under international quality standards, ensuring operational continuity, security, and efficiency for organizations across all sectors.'}
            </motion.p>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="mb-10 text-lg text-white/70"
            >
              {isEs 
                ? 'Entregando Soluciones de Infraestructura en toda Florida'
                : 'Delivering Infrastructure Solutions Across Florida'}
            </motion.p>

            {/* Service Icons - EXACT from Abacus: rgba(255,255,255,0.125) background */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex flex-wrap justify-center gap-6 sm:gap-8 lg:gap-12"
            >
              {services.map((service) => (
                <Link
                  key={service.id}
                  to={`/services/${service.id}`}
                  className="group flex flex-col items-center"
                >
                  <div 
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mb-2 transition-all group-hover:scale-110"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.125)' }}
                  >
                    <service.icon className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </div>
                  <span className="text-white/90 text-sm sm:text-base font-medium group-hover:text-white transition-colors">
                    {service.title.split(' ')[0]}
                  </span>
                </Link>
              ))}
            </motion.div>

          </div>
        </div>
      </section>

      {/* Services Section - WHITE background with EXACT Abacus colors */}
      <section className="py-20 bg-white" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="mb-4" style={{ color: 'rgb(0, 51, 102)' }}>
              {isEs ? 'Nuestros Servicios' : 'Our Services'}
            </h2>
            <p className="text-lg" style={{ color: 'rgb(107, 114, 128)' }}>
              {isEs 
                ? 'Soluciones integrales de bajo voltaje y telecomunicaciones'
                : 'Comprehensive low voltage and telecommunications solutions'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="h-full"
              >
                <Link to={`/services/${service.id}`}>
                  <div className="group h-full rounded-lg p-6 shadow-md hover:shadow-xl transition-all duration-300 bg-white border-2 border-transparent hover:border-[#00A651]">
                    <div className="mb-4 inline-flex rounded-lg p-3 transition-colors duration-300" style={{ backgroundColor: 'rgba(0, 166, 81, 0.082)' }}>
                      <service.icon className="h-8 w-8 transition-transform duration-300 group-hover:scale-110" style={{ color: 'rgb(0, 166, 81)' }} />
                    </div>
                    <h3 className="mb-2 text-xl font-bold" style={{ color: 'rgb(31, 41, 55)' }}>
                      {isEs ? service.titleEs : service.title}
                    </h3>
                    <p className="text-sm leading-relaxed" style={{ color: 'rgb(107, 114, 128)' }}>
                      {isEs ? service.descriptionEs : service.description}
                    </p>
                    <div className="mt-4 flex items-center text-sm font-medium" style={{ color: 'rgb(0, 51, 102)' }}>
                      <span className="group-hover:underline">{isEs ? 'Saber más' : 'Learn more'}</span>
                      <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas - EXACT from Abacus */}
      <section className="py-16" style={{ backgroundColor: 'rgba(0, 166, 81, 0.03)' }} data-testid="service-areas-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-2xl md:text-3xl font-bold" style={{ color: 'rgb(0, 51, 102)' }}>
              {isEs 
                ? 'Entregando Soluciones de Infraestructura en toda Florida'
                : 'Delivering Infrastructure Solutions Across Florida'}
            </h2>
          </motion.div>

          {/* City Cards - EXACT from Abacus */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
            {floridaCities.map((city, index) => (
              <motion.div
                key={city.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="text-center p-4 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow"
              >
                <MapPin className="h-6 w-6 mx-auto mb-2" style={{ color: 'rgb(0, 166, 81)' }} />
                <p className="font-semibold text-sm" style={{ color: 'rgb(31, 41, 55)' }}>{city.name}</p>
              </motion.div>
            ))}
          </div>

          {/* Projects Carousel */}
          <div className="relative">
            <div className="text-center mb-4">
              <h3 className="text-lg font-bold" style={{ color: 'rgb(0, 51, 102)' }}>
                {isEs ? 'Nuestros Proyectos' : 'Our Projects'}
              </h3>
            </div>
            <div className="flex gap-3 overflow-x-auto scrollbar-hide px-2 py-2" style={{ scrollBehavior: 'smooth' }}>
              {projectImages.map((project, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 relative rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all hover:scale-105 cursor-pointer"
                  style={{ width: '180px', height: '120px' }}
                >
                  <img
                    src={project.src}
                    alt={isEs ? project.titleEs : project.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                    <p className="text-white text-xs font-medium text-center truncate">
                      {isEs ? project.titleEs : project.title}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-6">
              <Link to="/service-areas">
                <Button 
                  className="hover:opacity-90"
                  style={{ backgroundColor: 'rgb(255, 107, 0)', color: 'rgb(255, 255, 255)' }}
                >
                  {isEs ? 'Ver Mapa de Cobertura Completo' : 'View Full Coverage Map'}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Why Choose Us - EXACT from Abacus */}
      <section className="py-20 bg-white" data-testid="stats-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="mb-4" style={{ color: 'rgb(0, 51, 102)' }}>
              {isEs ? '¿Por Qué Elegirnos?' : 'Why Choose Us'}
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow"
                style={{ backgroundColor: 'rgba(0, 51, 102, 0.02)' }}
              >
                <div className="text-4xl font-bold mb-2" style={{ color: 'rgb(0, 51, 102)' }}>
                  {stat.value}
                </div>
                <p className="text-sm font-medium" style={{ color: 'rgb(107, 114, 128)' }}>
                  {isEs ? stat.labelEs : stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects - EXACT from Abacus */}
      <section className="py-20" style={{ backgroundColor: 'rgba(0, 51, 102, 0.02)' }} data-testid="featured-projects">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="mb-4" style={{ color: 'rgb(0, 51, 102)' }}>
              {isEs ? 'Proyectos Recientes' : 'Recent Projects'}
            </h2>
            <p className="text-lg" style={{ color: 'rgb(107, 114, 128)' }}>
              {isEs ? 'Trabajo de calidad entregado a tiempo' : 'Quality work delivered on time'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {featuredProjects.map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="h-full"
              >
                <div className="group h-full overflow-hidden rounded-lg bg-white shadow-md hover:shadow-xl transition-all duration-300">
                  <div className="relative aspect-video bg-gray-200 overflow-hidden">
                    <img
                      src={project.image}
                      alt={isEs ? project.titleEs : project.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <div className="mb-2">
                      <span 
                        className="inline-block rounded-full px-3 py-1 text-xs font-medium"
                        style={{ backgroundColor: 'rgba(0, 166, 81, 0.082)', color: 'rgb(0, 166, 81)' }}
                      >
                        {project.category}
                      </span>
                    </div>
                    <h3 className="mb-2 text-xl font-bold line-clamp-2" style={{ color: 'rgb(31, 41, 55)' }}>
                      {isEs ? project.titleEs : project.title}
                    </h3>
                    <p className="mb-4 text-sm line-clamp-3" style={{ color: 'rgb(107, 114, 128)' }}>
                      {isEs ? project.descriptionEs : project.description}
                    </p>
                    <div className="flex items-center justify-between text-xs" style={{ color: 'rgb(156, 163, 175)' }}>
                      <div className="flex items-center space-x-1">
                        <MapPin className="h-3 w-3" />
                        <span>{project.location}</span>
                      </div>
                      <span>{project.date}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/projects">
              <Button 
                className="h-11 rounded-md px-8"
                style={{ backgroundColor: 'rgb(0, 51, 102)', color: 'rgb(255, 255, 255)' }}
              >
                {isEs ? 'Ver Todos los Proyectos' : 'View All Projects'}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials - EXACT from Abacus */}
      <section className="py-20 bg-white" data-testid="testimonials-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="mb-4" style={{ color: 'rgb(0, 51, 102)' }}>
              {isEs ? 'Lo Que Dicen Nuestros Clientes' : 'What Our Clients Say'}
            </h2>
            <p className="text-lg" style={{ color: 'rgb(107, 114, 128)' }}>
              {isEs ? 'Confiado por empresas en toda Florida' : 'Trusted by businesses across Florida'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="h-full"
              >
                <div className="h-full rounded-lg p-6 shadow-md bg-white relative">
                  <div className="flex mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-4 w-4" fill="#00A651" stroke="#00A651" />
                    ))}
                  </div>
                  <p className="mb-4 text-sm leading-relaxed italic" style={{ color: 'rgb(107, 114, 128)' }}>
                    "{isEs ? testimonial.textEs : testimonial.text}"
                  </p>
                  <div className="border-t pt-4" style={{ borderColor: 'rgba(156, 163, 175, 0.19)' }}>
                    <p className="font-semibold" style={{ color: 'rgb(31, 41, 55)' }}>{testimonial.name}</p>
                    <p className="text-xs" style={{ color: 'rgb(156, 163, 175)' }}>{testimonial.company}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Marcas y Productos Section - Compact with Descriptions */}
      <section className="py-10" style={{ backgroundColor: '#001a33' }} data-testid="brands-section">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-6"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-1">
              {isEs ? 'Marcas y Productos' : 'Brands & Products'}
            </h2>
            <p className="text-white/70 text-sm">
              {isEs ? 'Trabajamos con las mejores marcas de la industria' : 'We work with the best brands in the industry'}
            </p>
          </motion.div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 max-w-6xl">
            {/* CommScope */}
            <Link to="/projects">
              <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.05 }} 
                className="rounded-md p-2 cursor-pointer hover:bg-[#0d3258] transition-colors" style={{ backgroundColor: '#0a2847' }}>
                <div className="h-8 flex items-center justify-center mb-1">
                  <span className="text-white text-xs tracking-wide" style={{ fontFamily: 'Arial, sans-serif' }}>
                    <span className="font-bold" style={{ color: '#00a0df' }}>C</span>
                    <span className="font-light">OMMSC</span>
                    <span className="font-bold" style={{ color: '#00a0df' }}>O</span>
                    <span className="font-light">PE</span>
                  </span>
                </div>
                <p className="text-white/60 text-[8px] leading-tight text-center">
                  {isEs ? 'Fibra óptica, data center' : 'Fiber optic, data center'}
                </p>
              </motion.div>
            </Link>

            {/* Panduit */}
            <Link to="/projects">
              <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} 
                className="rounded-md p-2 cursor-pointer hover:bg-[#0d3258] transition-colors" style={{ backgroundColor: '#0a2847' }}>
                <div className="h-8 flex items-center justify-center mb-1">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full border border-orange-500 flex items-center justify-center">
                      <div className="w-1 h-1 bg-orange-500 rounded-full"></div>
                    </div>
                    <span className="text-white text-[10px] font-bold tracking-wide">PANDUIT</span>
                  </div>
                </div>
                <p className="text-white/60 text-[8px] leading-tight text-center">
                  {isEs ? 'Cableado estructurado' : 'Structured cabling'}
                </p>
              </motion.div>
            </Link>

            {/* Corning */}
            <Link to="/projects">
              <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }} 
                className="rounded-md p-2 cursor-pointer hover:bg-[#0d3258] transition-colors" style={{ backgroundColor: '#0a2847' }}>
                <div className="h-8 flex items-center justify-center mb-1">
                  <span className="text-white text-xs tracking-widest">CORNING</span>
                </div>
                <p className="text-white/60 text-[8px] leading-tight text-center">
                  {isEs ? 'Fibra monomodo/multimodo' : 'Single/multi-mode fiber'}
                </p>
              </motion.div>
            </Link>

            {/* Fluke */}
            <Link to="/projects">
              <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} 
                className="rounded-md p-2 cursor-pointer hover:bg-[#0d3258] transition-colors" style={{ backgroundColor: '#0a2847' }}>
                <div className="h-8 flex items-center justify-center mb-1">
                  <span className="text-white text-xs font-bold tracking-wider">FLUKE.</span>
                </div>
                <p className="text-white/60 text-[8px] leading-tight text-center">
                  {isEs ? 'Certificación de redes' : 'Network certification'}
                </p>
              </motion.div>
            </Link>

            {/* Axis */}
            <Link to="/projects">
              <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.25 }} 
                className="rounded-md p-2 cursor-pointer hover:bg-[#0d3258] transition-colors" style={{ backgroundColor: '#0a2847' }}>
                <div className="h-8 flex items-center justify-center mb-1">
                  <span className="text-white text-xs font-bold tracking-widest">AXIS</span>
                </div>
                <p className="text-white/60 text-[8px] leading-tight text-center">
                  {isEs ? 'Cámaras IP vigilancia' : 'IP surveillance cameras'}
                </p>
              </motion.div>
            </Link>

            {/* Hikvision */}
            <Link to="/projects">
              <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }} 
                className="rounded-md p-2 cursor-pointer hover:bg-[#0d3258] transition-colors" style={{ backgroundColor: '#0a2847' }}>
                <div className="h-8 flex items-center justify-center mb-1">
                  <span className="text-xs tracking-wide">
                    <span className="text-white font-bold">HIK</span><span className="text-red-500 font-bold">VISION</span>
                  </span>
                </div>
                <p className="text-white/60 text-[8px] leading-tight text-center">
                  {isEs ? 'CCTV, NVR/DVR' : 'CCTV, NVR/DVR'}
                </p>
              </motion.div>
            </Link>

            {/* Avigilon */}
            <Link to="/projects">
              <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.35 }} 
                className="rounded-md p-2 cursor-pointer hover:bg-[#0d3258] transition-colors" style={{ backgroundColor: '#0a2847' }}>
                <div className="h-8 flex items-center justify-center mb-1">
                  <span className="text-white text-[10px] tracking-[0.15em] font-light">AVIGILON</span>
                </div>
                <p className="text-white/60 text-[8px] leading-tight text-center">
                  {isEs ? 'Video analytics HD' : 'HD video analytics'}
                </p>
              </motion.div>
            </Link>

            {/* Cisco */}
            <Link to="/projects">
              <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.4 }} 
                className="rounded-md p-2 cursor-pointer hover:bg-[#0d3258] transition-colors" style={{ backgroundColor: '#0a2847' }}>
                <div className="h-8 flex items-center justify-center mb-1">
                  <div className="text-center">
                    <div className="flex justify-center gap-[1px] mb-0.5">
                      {[...Array(9)].map((_, i) => (
                        <div key={i} className="w-[2px] bg-white" style={{ height: `${4 + Math.sin(i * 0.7) * 3}px` }}></div>
                      ))}
                    </div>
                    <span className="text-white text-[10px] font-bold tracking-wider">CISCO</span>
                  </div>
                </div>
                <p className="text-white/60 text-[8px] leading-tight text-center">
                  {isEs ? 'Redes, switches, routers' : 'Networks, switches, routers'}
                </p>
              </motion.div>
            </Link>

            {/* Ubiquiti */}
            <Link to="/projects">
              <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.45 }} 
                className="rounded-md p-2 cursor-pointer hover:bg-[#0d3258] transition-colors" style={{ backgroundColor: '#0a2847' }}>
                <div className="h-8 flex items-center justify-center mb-1">
                  <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center">
                    <span className="text-white text-[10px] font-bold">U</span>
                  </div>
                </div>
                <p className="text-white/60 text-[8px] leading-tight text-center">
                  {isEs ? 'WiFi access points' : 'WiFi access points'}
                </p>
              </motion.div>
            </Link>

            {/* HID */}
            <Link to="/projects">
              <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.5 }} 
                className="rounded-md p-2 cursor-pointer hover:bg-[#0d3258] transition-colors" style={{ backgroundColor: '#0a2847' }}>
                <div className="h-8 flex items-center justify-center mb-1">
                  <div className="bg-blue-600 px-2 py-0.5 rounded-sm">
                    <span className="text-white text-[10px] font-bold">HID</span>
                  </div>
                </div>
                <p className="text-white/60 text-[8px] leading-tight text-center">
                  {isEs ? 'Control de acceso' : 'Access control'}
                </p>
              </motion.div>
            </Link>

            {/* HPE Aruba */}
            <Link to="/projects">
              <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.55 }} 
                className="rounded-md p-2 cursor-pointer hover:bg-[#0d3258] transition-colors" style={{ backgroundColor: '#0a2847' }}>
                <div className="h-8 flex items-center justify-center mb-1">
                  <span className="text-white text-[8px] font-light">HPE </span>
                  <span className="text-orange-500 text-[10px] font-bold">aruba</span>
                </div>
                <p className="text-white/60 text-[8px] leading-tight text-center">
                  {isEs ? 'WiFi empresarial' : 'Enterprise WiFi'}
                </p>
              </motion.div>
            </Link>

            {/* Fortinet */}
            <Link to="/projects">
              <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.6 }} 
                className="rounded-md p-2 cursor-pointer hover:bg-[#0d3258] transition-colors" style={{ backgroundColor: '#0a2847' }}>
                <div className="h-8 flex items-center justify-center mb-1">
                  <span className="text-xs">
                    <span className="text-red-500 font-bold">F</span>
                    <span className="text-white font-bold text-[10px]">ORTINET</span>
                  </span>
                </div>
                <p className="text-white/60 text-[8px] leading-tight text-center">
                  {isEs ? 'Firewall, ciberseguridad' : 'Firewall, cybersecurity'}
                </p>
              </motion.div>
            </Link>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-white/50 text-xs mt-4"
          >
            {isEs 
              ? 'Certificados en instalación y mantenimiento profesional de equipos'
              : 'Certified in professional equipment installation and maintenance'}
          </motion.p>
        </div>
      </section>

      {/* CTA Section - Reduced 30% */}
      <section className="py-14" style={{ background: 'linear-gradient(135deg, rgb(0, 51, 102) 0%, rgba(0, 166, 81, 0.25) 100%)' }} data-testid="cta-section">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <div>
            <h2 className="mb-4 text-white text-2xl sm:text-3xl font-bold">
              {isEs ? '¿Listo para Comenzar?' : 'Ready to Get Started?'}
            </h2>
            <p className="mb-6 text-lg text-white/90">
              {isEs 
                ? 'Contáctenos hoy para una consulta y cotización gratuita'
                : 'Contact us today for a free consultation and quote'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center mb-6">
              <Link to="/quote">
                <Button 
                  className="h-10 rounded-md text-base px-6 py-4 font-medium"
                  style={{ backgroundColor: 'rgb(211, 47, 47)', color: 'rgb(255, 255, 255)' }}
                  data-testid="cta-quote-btn"
                >
                  {isEs ? 'Cotización' : 'Get Quote'}
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  variant="outline"
                  className="h-10 rounded-md text-base px-6 py-4 font-medium border-2 bg-white/10 backdrop-blur-sm hover:bg-white/20"
                  style={{ borderColor: 'rgb(255, 255, 255)', color: 'rgb(255, 255, 255)' }}
                >
                  {isEs ? 'Contactar' : 'Contact'}
                </Button>
              </Link>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center text-white">
              <a href="tel:+17869813760" className="flex items-center gap-2 hover:text-white/80 transition-colors">
                <Phone className="h-4 w-4" />
                <span className="text-base">786-981-3760</span>
              </a>
              <span className="hidden sm:inline text-white/40">|</span>
              <a href="mailto:info@iqeslowvoltage.com" className="flex items-center gap-2 hover:text-white/80 transition-colors">
                <Mail className="h-4 w-4" />
                <span className="text-base">info@iqeslowvoltage.com</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
